// The proof on screen B and on the website's wall (owner, 2026-10-25: "We really have to highlight they
// are getting prices for over 200 barns live. No one else does this.")
//
// Two reads, both the app's existing ones, never a new RPC:
//   · fetchBarnIndex()      — every reporting barn → the "200+" count
//   · fetchCattleTicker()   — today's lots, every band → the latest lot per barn
//   · fetchLiveBarnSlugs()  — which of those barns is selling right now
// Nothing here throws: a failed read draws the fallback count and no strip,
// and the paywall still sells. The figures are the real ones at that moment,
// which is the strongest proof there is — a canned list would go stale.

import { fetchBarnIndex, fetchCattleTicker, fetchLiveBarnSlugs, todayCentral, type TickerPayload } from "@/lib/rbCattleTicker";
import { barnSlug } from "@/lib/barnSlug";

/** "Fort Scott Livestock Market" → "Fort Scott": the strip is 130px wide, and
 *  the town is what a cattleman calls the barn anyway. */
export function shortBarn(name: string): string {
  const s = name.replace(/\b(Livestock|Stockyards?|Auction|Market(ing|s)?|Commission|Co\.?|Company|Sales?|Exchange|Inc\.?|LLC)\b\.?/gi, " ").replace(/\s+/g, " ").trim();
  return s || name;
}

export interface ProofLot {
  /** The barn's full name. */
  barn: string;
  /** The short form for the strip. */
  short: string;
  state: string;
  /** "$426.00" */
  priceLabel: string;
  /** "5-wt steers · 18 hd" */
  line: string;
  live: boolean;
  at: string;
}

export interface PaywallProof {
  /** Reporting barns, or null when the read failed. */
  barnCount: number | null;
  /** Latest lot per barn, live barns first, newest first. */
  lots: ProofLot[];
}

/** "200+" — the count rounded DOWN to a round figure, so it is never a claim
 *  the data cannot back. Under 20 barns it is just the number. */
export function barnCountLabel(n: number | null, fallback = "200+"): string {
  if (n === null || !Number.isFinite(n) || n <= 0) return fallback;
  if (n < 20) return String(n);
  const step = n < 100 ? 10 : n < 1000 ? 50 : 100;
  return `${Math.floor(n / step) * step}+`;
}

/** The latest lot at each barn on the board, live barns first. Pure, for tests. */
export function proofLots(t: TickerPayload | null, live: Set<string>, max = 8): ProofLot[] {
  if (!t) return [];
  const byBarn = new Map<string, ProofLot>();
  for (const b of t.bands) {
    for (const l of b.lots) {
      if (!l.barn || !l.priceLabel) continue;
      const key = `${l.state}|${l.barn}`;
      const prev = byBarn.get(key);
      if (prev && prev.at >= l.at) continue;
      const slug = barnSlug(l.barn, l.state);
      const wt = `${Math.floor(b.band / 100)}-wt`;
      const cls = l.cattleType === "Heifers" ? "heifers" : l.cattleType === "Steers" ? "steers" : l.cattleType.toLowerCase();
      byBarn.set(key, {
        barn: l.barn, short: shortBarn(l.barn), state: l.state, priceLabel: l.priceLabel, at: l.at,
        live: !!slug && live.has(slug),
        line: `${wt} ${cls} · ${l.headLabel}`,
      });
    }
  }
  return [...byBarn.values()]
    .sort((a, b) => Number(b.live) - Number(a.live) || (a.at < b.at ? 1 : a.at > b.at ? -1 : 0))
    .slice(0, max);
}

export async function fetchPaywallProof(): Promise<PaywallProof> {
  const [barns, ticker, live] = await Promise.all([
    fetchBarnIndex().catch(() => []),
    fetchCattleTicker({}, todayCentral()).catch(() => null),
    fetchLiveBarnSlugs().catch(() => new Set<string>()),
  ]);
  return { barnCount: barns.length || null, lots: proofLots(ticker, live) };
}
